import { useSelector } from 'react-redux';
import React, { useRef, useEffect } from 'react';
import * as constantsViewer from '../../constants';
import { useSpring, a } from 'react-spring-beta/three';
import { Top } from './Top';
import { Shelf } from './Shelf';
import { Corners } from './Corners';
import { getShelvesWithPositions } from '../../helpers';
import { BottomSupport } from './BottomSupport';
import { makeSelectIsShelvesMode } from 'containers/Configurator/selectors/general';
import { createStructuredSelector } from 'reselect';
import { makeSelectShelvesLevelSelected } from 'containers/Configurator/selectors/selection';
import { makeSelectIsLastShelvesSelected } from 'containers/Configurator/selectors/history';
import { makeSelectConfiguratorMode } from 'containers/ModeBar/selectors';
import { UpperShelf } from '../pieces/UpperShelf';

const stateProps = createStructuredSelector({
  isShelvesMode: makeSelectIsShelvesMode(),
  isLastLevelShelfSelected: makeSelectIsLastShelvesSelected(),
  shelvesLevelSelected: makeSelectShelvesLevelSelected(),
  configuratorMode: makeSelectConfiguratorMode(),
});

export function Cabinet({ cabinet, multiCabinets, position }) {
  const group = useRef();

  const [spring, set] = useSpring(() => ({
    position: position,
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  const {
    isShelvesMode,
    shelvesLevelSelected,
    isLastLevelShelfSelected,
    configuratorMode,
  } = useSelector(stateProps);
  const cabinetDepth = cabinet.depth;
  const cabinetWidth = cabinet.width;
  const cabinetHeight = cabinet.height + cabinet.groundOffset;

  let cornersHeight = cabinet.getCorner('FL').height + 3;

  const modelCabinetHeight = 450;
  const modelShelfHeight = 370 + 40;

  const shelvesWithPositions = getShelvesWithPositions(cabinet);

  const heightGap = cabinet.upperShelf ? cabinet.upperShelf.height : 0;

  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        position: position,
      });
    }
  });

  return (
    <a.group ref={group} position={spring.position}>
      {cabinet.upperShelf && (
        <UpperShelf
          upperShelf={cabinet.upperShelf}
          cabinetId={cabinet.id}
          configuratorMode={configuratorMode}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          cabinetHeight={cabinetHeight}
          unselected={isShelvesMode}
        />
      )}
      <Top
        top={cabinet.top}
        cabinetId={cabinet.id}
        configuratorMode={configuratorMode}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        cabinetHeight={cabinetHeight - 2 * heightGap}
        unselected={!isLastLevelShelfSelected}
      />

      {shelvesWithPositions.map(positionShelf => {
        const shelf = positionShelf.shelf;

        return (
          <Shelf
            multiCabinets={multiCabinets}
            cabinetId={cabinet.id}
            configuratorMode={configuratorMode}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            shelf={shelf}
            unselected={
              shelvesLevelSelected.length > 0 &&
              !shelvesLevelSelected.includes(shelf.level) &&
              isShelvesMode
            }
            position={[
              0,
              positionShelf.position -
                (cabinetHeight - modelCabinetHeight) / 2 +
                (shelf.realHeight - modelShelfHeight) / 2,
              0,
            ]}
            key={shelf.level}
          />
        );
      })}

      <Corners
        corners={cabinet.corners}
        cornersHeight={cornersHeight}
        cabinetHeight={cabinetHeight - 2 * heightGap}
        cabinetWidth={cabinetWidth}
        cabinetDepth={cabinetDepth}
        withHookSuspension={cabinet.support === 'H'}
        unselected={isShelvesMode}
      />

      {cabinet.bottomSupportHeight > 0 &&
      (cabinet.support === 'F' ||
        cabinet.support === 'A' ||
        cabinet.support === 'B' ||
        cabinet.support === 'W') ? (
        <BottomSupport
          type={cabinet.support}
          cabinet={cabinet}
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          cornersHeight={cornersHeight}
          cabinetHeight={cabinetHeight}
          bottomSupportHeight={cabinet.bottomSupportHeight}
          unselected={isShelvesMode}
        />
      ) : null}
    </a.group>
  );
}
